import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { projectService } from '@/services/projectService';
import { Project } from '@/types';
import { useDebounce } from '@/hooks/useDebounce';

interface ProjectSearchSelectProps {
  value: string; // IRI format: /api/projects/{id}
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  disabled?: boolean;
}

export function ProjectSearchSelect({ value, onChange, onBlur, error, disabled }: ProjectSearchSelectProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const debouncedSearch = useDebounce(searchQuery, 300);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Extract project ID from IRI
  const projectId = value ? parseInt(value.replace('/api/projects/', ''), 10) : null;

  // Fetch selected project when value changes (for edit mode)
  const { data: selectedProjectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => {
      if (!projectId) return null;
      return projectService.getProject(projectId);
    },
    enabled: !!projectId,
  });

  // Fetch projects based on search query
  const { data: projectsResponse, isLoading } = useQuery({
    queryKey: ['projects', 'search', debouncedSearch],
    queryFn: () => projectService.getProjects({ page: 1, itemsPerPage: 20, search: debouncedSearch }),
    enabled: isOpen || debouncedSearch.length > 0,
  });

  const projects = projectsResponse?.data || [];

  // Update selected project when data is loaded or value changes
  useEffect(() => {
    if (selectedProjectData && selectedProjectData.id === projectId) {
      // Only update if the selected project doesn't match or is null
      if (!selectedProject || selectedProject.id !== selectedProjectData.id) {
        setSelectedProject(selectedProjectData);
        setSearchQuery(selectedProjectData.title || '');
      }
    } else if (!projectId) {
      // Reset when value is cleared
      if (selectedProject) {
        setSelectedProject(null);
        setSearchQuery('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectData, projectId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectProject = (project: Project) => {
    setSelectedProject(project);
    setSearchQuery(project.title || '');
    onChange(`/api/projects/${project.id}`);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
    setIsOpen(true);
    
    // Clear selection if input is cleared
    if (!newQuery) {
      setSelectedProject(null);
      onChange('');
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={searchQuery}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={onBlur}
        disabled={disabled}
        placeholder="Rechercher un projet..."
        className={`w-full px-3 py-2 border ${
          error ? 'border-red-500' : 'border-slate-300 dark:border-[#1E293B]'
        } rounded-lg bg-white dark:bg-[#0B1116] focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed`}
      />
      
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-[#0B1116] border border-slate-300 dark:border-[#1E293B] rounded-lg shadow-lg max-h-60 overflow-auto">
          {isLoading ? (
            <div className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400">
              Recherche en cours...
            </div>
          ) : projects.length === 0 ? (
            <div className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400">
              {debouncedSearch ? 'Aucun projet trouvé' : 'Tapez pour rechercher un projet'}
            </div>
          ) : (
            projects.map((project) => (
              <button
                key={project.id}
                type="button"
                onClick={() => handleSelectProject(project)}
                className={`w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-[#1E293B] transition-colors ${
                  selectedProject?.id === project.id ? 'bg-primary/10 dark:bg-primary/20' : ''
                }`}
              >
                <div className="font-medium text-slate-900 dark:text-slate-100">
                  {project.title}
                </div>
                {project.city && (
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {project.city}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      )}
      
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
}
